import React, { useState } from 'react';
import { AiOutlineCheck } from 'react-icons/ai';
import FareDetailsDialog from './FareDetailsDialog';
import UpgradeDialog from './UpgradeDialog';

const FareCard = ({ 
  fare, 
  onRefundChange, 
  selectedRefundOption,
  onSelect,
  flightData,
  tripType = 'roundTrip'
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false);
  const getCardIndicatorColor = (type) => {
    const colors = {
      'Main': '#0860c4',
      'Main Plus': '#0860c4', 
      'Main Select': '#0860c4',
      'Premium Economy': '#0860c4',
      'Business': '#03264e',
      'Flagship® Business': '#03264e',
      'Flagship® First': '#03264e',
      'First': '#03264e',
      'Basic Economy': '#5e6d7a',
    };
    return colors[type] || '#0860c4';
  };

  const getTextColor = (type) => {
    const colors = {
      'Main': '#0860c4',
      'Main Plus': '#0860c4',
      'Main Select': '#0860c4',
      'Premium Economy': '#0860c4',
      'Business': '#03264e', 
      'Flagship® Business': '#03264e',
      'Flagship® First': '#03264e',
      'First': '#03264e',
      'Basic Economy': '#5e6d7a',
    };
    return colors[type] || '#0860c4';
  };

  const getBenefitIcon = (type) => {
    if (type === 'checkmark') {
      return <AiOutlineCheck className="text-green-600 w-4 h-4 mt-0.5 flex-shrink-0" />;
    } else if (type === 'fee') {
      return (
        <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
          <img 
            src="/content/images/farecard/cash.png" 
            alt="Fee" 
            className="w-4 h-4"
          />
        </div>
      );
    } else if (type === 'restriction') {
      return (
        <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
          <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      );
    } else {
      return (
        <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
          <span className="text-gray-600 font-bold text-sm">$</span>
        </div>
      );
    }
  };

  const cardColor = getCardIndicatorColor(fare.type);
  const textColor = getTextColor(fare.type);

  return (
    <div className="flex-shrink-0 bg-white border border-gray-200 rounded shadow-lg overflow-hidden flex flex-col mb-4" 
         style={{ width: '300px', minWidth: '300px', height: '700px' }}>
      
      {/* Top color indicator bar */}
      <div 
        className="h-3 w-full rounded-t"
        style={{ backgroundColor: cardColor }}
      />
      
      <div className="pt-4 pb-2 px-4 flex-1 flex flex-col">
        
        {/* Trip type and tag section */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-gray-600">{tripType === 'oneWay' ? 'One way' : 'Round trip'}</span>
          {fare.tag && (
            <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
              {fare.tag}
            </span>
          )}
        </div>

        {/* Fare type and info icon */}
        <div className="flex items-center gap-2 mb-3">
          <h3 
            className="text-xl font-bold"
            style={{ color: textColor }}
          >
            {fare.type}
          </h3>
          <svg 
            width="20" 
            height="20" 
            viewBox="0 0 16 16" 
            fill="currentColor" 
            xmlns="http://www.w3.org/2000/svg" 
            aria-hidden="true"
            className="w-5 h-5 cursor-pointer hover:opacity-70 transition-opacity text-aa-blue"
            onClick={() => setIsDialogOpen(true)}
          >
            <g clipPath="url(#clip0_7669_34263)">
              <path d="M8 1C4.15 1 1 4.15 1 8C1 11.85 4.15 15 8 15C11.85 15 15 11.85 15 8C15 4.15 11.85 1 8 1ZM8 14C4.7 14 2 11.3 2 8C2 4.7 4.7 2 8 2C11.3 2 14 4.7 14 8C14 11.3 11.3 14 8 14ZM8.5 6.5V12H7.5V6.5H8.5ZM8.75 4.75C8.75 5.15 8.4 5.5 8 5.5C7.6 5.5 7.25 5.15 7.25 4.75C7.25 4.35 7.6 4 8 4C8.4 4 8.75 4.35 8.75 4.75Z" fill="currentColor"></path>
            </g>
            <defs>
              <clipPath id="clip0_7669_34263">
                <rect width="16" height="16" fill="white"></rect>
              </clipPath>
            </defs>
          </svg>
        </div>

        {/* Price section */}
        <div className="mb-4">
          <span className="text-2xl font-bold text-gray-900">{fare.price}</span>
          <span className="text-sm text-gray-600 ml-2">{fare.priceDetail}</span>
        </div>

        {/* Divider */}
        <div className="border-b border-gray-200 mb-5"></div>

        {/* Benefits section */}
        <div className="space-y-3 mb-6 flex-1">
          {fare.benefits.map((benefit, idx) => (
            <div key={idx} className="flex items-start gap-3">
              {getBenefitIcon(benefit.type)}
              <span className="text-sm text-gray-700 leading-relaxed">
                {benefit.text}
              </span>
            </div>
          ))}
        </div>

        {/* Refund options section */}
        <div className="bg-blue-50 rounded px-2 py-2 mb-4">
          {fare.refundOptions.map((option, idx) => (
            <div key={idx} className="mb-1">
              {fare.refundOptions.length > 1 ? (
                <label className="flex items-start gap-2 cursor-pointer py-1">
                  <input
                    type="radio"
                    name={`refund-${fare.id}`}
                    checked={selectedRefundOption === option.id || option.selected}
                    onChange={() => onRefundChange(fare.id, option.id)}
                    className="mt-1 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <span className="text-sm text-gray-700">{option.label}</span>
                    {option.price && (
                      <span className="text-sm font-medium text-gray-900 ml-2">
                        {option.price}
                      </span>
                    )}
                  </div>
                </label>
              ) : (
                <div className="flex items-start gap-2 py-1">
                  {option.label === "No refunds allowed" ? (
                    <svg className="w-4 h-4 text-red-600 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <AiOutlineCheck className="text-green-600 w-4 h-4 mt-1 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <span className="text-sm text-gray-700">{option.label}</span>
                    {option.price && (
                      <span className="text-sm font-medium text-gray-900 ml-2">
                        {option.price}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Select button */}
        <button 
          onClick={() => {
            if (fare.type === 'Basic Economy' || fare.type === 'Main') {
              setIsUpgradeDialogOpen(true);
            } else {
              onSelect(fare);
            }
          }}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded transition-colors text-sm"
        >
          <span>Select</span>
          <span className="sr-only">
            Select {tripType === 'oneWay' ? 'One way' : 'Round trip'} {fare.type} fare for {fare.price} departing from {flightData?.departureCode} to {flightData?.arrivalCode}
          </span>
        </button>
      </div>

      {/* Fare Details Dialog */}
      <FareDetailsDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        fareType={fare.type}
      />

      {/* Upgrade Dialog for Basic Economy */}
      <UpgradeDialog
        isOpen={isUpgradeDialogOpen}
        onClose={() => setIsUpgradeDialogOpen(false)}
        flightData={flightData}
        fareData={fare}
        tripType={tripType}
        onUpgrade={() => {
          setIsUpgradeDialogOpen(false);
          // Handle upgrade based on current fare type
          let upgradedFare;
          
          if (fare.type === 'Basic Economy') {
            upgradedFare = {
              ...fare,
              type: 'Main',
              price: flightData.mainPrice,
              id: 'main'
            };
          } else if (fare.type === 'Main') {
            upgradedFare = {
              ...fare,
              type: 'Main Plus',
              price: flightData.mainPlusPrice || (fare.price + '$122'),
              id: 'main-plus'
            };
          } else {
            // Default upgrade scenario
            upgradedFare = {
              ...fare,
              type: 'Main Plus',
              price: flightData.mainPlusPrice || (fare.price + '$122'),
              id: 'main-plus'
            };
          }
          
          onSelect(upgradedFare);
        }}
        onAcceptRestrictions={() => {
          setIsUpgradeDialogOpen(false);
          onSelect(fare);
        }}
      />
    </div>
  );
};

export default FareCard;